// Provider keys for the dashboard's provider filter (2026-09-30).
//
// A dashboard row names its provider in linked_provider, falling back to
// providername. Those strings disagree for the same person: the Athena sync and
// the Select Provider modal store the provider_master short name ("Dr. Madhu"),
// while patient-app bookings and reschedules stored the full Athena name
// ("Madhu Pamganamamula, MD"). Filtering on the raw string listed one provider
// twice and split their patients between the two entries.
//
// providerKey() maps every row to ONE name:
//   1. linked_provider that is a provider_master short name (trimmed, any
//      letter case - older rows carry "jaime" / "oco") wins, in the
//      provider_master spelling: it is either the provider's own name or a
//      deliberate staff reassignment made through the Select Provider modal,
//      which must keep showing under the reassigned provider;
//   2. otherwise the provider_master short name for the row's providerid;
//   3. otherwise the raw string, as before.
//
// Pure module (no api import) so it can be checked with plain node:
//   node --no-warnings src/utils/providerDirectory.test.mjs

export const EMPTY_PROVIDER_DIRECTORY = Object.freeze({
  names: new Set(),
  byId: new Map(),
  byLowerName: new Map(),
  // provider filter by id (2026-10-08): name -> provider_master id
  idByLowerName: new Map(),
  idBySquash: new Map(),
  idByNorm: new Map(),
});

// GET /v1/{practiceid}/providers body.data: [{ providerid, providername }],
// where providerid is provider_master.id (what appointment rows carry).
export function buildProviderDirectory(list) {
  const names = new Set();
  const byId = new Map();
  const byLowerName = new Map(); // lower-cased name -> provider_master spelling
  // Name -> provider_master id, for the id-keyed filter (2026-10-08). A name
  // two different ids share maps to null (ambiguous: never used).
  const idByLowerName = new Map();
  const idBySquash = new Map();
  const idByNorm = new Map();
  const claim = (map, k, id) => {
    if (!k) return;
    if (!map.has(k)) map.set(k, id);
    else if (map.get(k) !== id) map.set(k, null);
  };
  (Array.isArray(list) ? list : []).forEach((p) => {
    const name = String(p?.providername ?? "").trim();
    if (!name) return;
    names.add(name);
    if (!byLowerName.has(name.toLowerCase())) byLowerName.set(name.toLowerCase(), name);
    const id = p?.providerid;
    if (id !== undefined && id !== null && id !== "") {
      const sid = String(id);
      byId.set(sid, name);
      claim(idByLowerName, name.toLowerCase(), sid);
      claim(idBySquash, squashProviderName(name), sid);
      claim(idByNorm, normProviderName(name), sid);
    }
  });
  return { names, byId, byLowerName, idByLowerName, idBySquash, idByNorm };
}

// The raw provider string the dashboard used before (unchanged precedence).
export function rawProviderName(row) {
  return (
    row?.linked_provider ||
    row?.providername ||
    row?.provider ||
    row?.provider_name ||
    [row?.providerfirstname, row?.providerlastname].filter(Boolean).join(" ") ||
    ""
  ).trim();
}

export function providerKey(row, directory = EMPTY_PROVIDER_DIRECTORY) {
  const dir = directory || EMPTY_PROVIDER_DIRECTORY;
  const linked = String(row?.linked_provider ?? "").trim();
  const named = linked && dir.byLowerName?.get(linked.toLowerCase());
  if (named) return named;
  const id = row?.providerid;
  if (id !== undefined && id !== null && id !== "") {
    const name = dir.byId.get(String(id));
    if (name) return name;
  }
  return rawProviderName(row);
}

// One providers request per practice per page load, shared by every caller.
// A failed load is forgotten, so the next caller (next mount) retries.
const directoryCache = new Map();

export function loadProviderDirectory(practiceid, fetchList) {
  const key = String(practiceid ?? "");
  if (!directoryCache.has(key)) {
    const pending = Promise.resolve()
      .then(() => fetchList(key))
      .then((res) => {
        // 200 with the list, or 201 with [] when the practice has none.
        if (!Array.isArray(res?.data)) {
          throw new Error("providers: unexpected response");
        }
        return buildProviderDirectory(res.data);
      })
      .catch((error) => {
        directoryCache.delete(key);
        throw error;
      });
    directoryCache.set(key, pending);
  }
  return directoryCache.get(key);
}

// Display label for a provider key (2026-10-07, owner: Frisco "should only
// show 'Dr. Malik'"). The key stays the provider_master short name (the
// filter selects and matches on it); only what is shown changes:
//   - a key that already carries the title keeps it ("Dr. Madhu");
//   - a key that is itself an MD/DO full name ("Reshmi Adupa, MD", a row no
//     provider_master name matched) becomes "Dr. <last name>";
//   - a two-or-more-word key becomes "Dr. <last name>" when one of the
//     provider's own rows names them as a physician (Athena's full name ends
//     in MD/DO, or starts with "Dr."), and that name has the same last name:
//     "Bobby Malik" + "Bobby A Malik, MD" -> "Dr. Malik";
//   - everything else is shown as is (NPs, "Procedures", "Claudia", ...).
// Derived from the loaded rows only: no provider is named in code. Used where
// practiceFeatures().providerFilterLabels is on (9928); callers elsewhere
// show the key.
// The degree: ", MD" / ", DO" (any case, also "M.D."), or " MD" / " DO" in
// capitals after a mixed-case name ("Bobby A Malik MD"). A bare " Do" is a
// surname ("Thanh Do"), and so is " DO" ending an all-caps name ("THANH DO"):
// neither may turn a provider into "Dr. ...".
const COMMA_DEGREE = /^(.*?),\s*(?:md|do|m\.d\.|d\.o\.)\.?$/i;
const SPACE_DEGREE = /^(.*?)\s+(?:MD|DO)\.?$/;
const physicianSuffix = (s) => {
  const comma = COMMA_DEGREE.exec(s);
  if (comma) return comma;
  const space = SPACE_DEGREE.exec(s);
  return space && /[a-z]/.test(space[1]) ? space : null;
};
const isPhysicianName = (s) => /^dr\.?\s/i.test(s) || Boolean(physicianSuffix(s));
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const tidy = (s) => String(s ?? "").replace(/\s+/g, " ").trim();
const lastWord = (s) => {
  const words = tidy(s).replace(/,/g, " ").split(" ").filter(Boolean);
  return words[words.length - 1] || "";
};

export function providerLabel(key, rawNames = []) {
  const k = tidy(key);
  if (!k) return "";
  if (/^dr\.?\s/i.test(k)) {
    const rest = k.replace(/^dr\.?\s+/i, "");
    const titled = physicianSuffix(rest); // "Dr. Bobby Malik MD"
    return titled && lastWord(titled[1]) ? `Dr. ${lastWord(titled[1])}` : `Dr. ${rest}`;
  }
  const own = physicianSuffix(k);
  if (own) {
    const last = lastWord(own[1]);
    return last ? `Dr. ${last}` : k;
  }
  if (k.split(" ").length < 2) return k;
  const last = lastWord(k);
  const sameLast = new RegExp(`(^|[\\s,.])${escapeRegExp(last)}($|[\\s,.])`, "i");
  const physician = (Array.isArray(rawNames) ? rawNames : [])
    .map(tidy)
    .some((raw) => isPhysicianName(raw) && sameLast.test(raw));
  return physician ? `Dr. ${last}` : k;
}

// Collapses options whose labels would collide back to their keys, and sorts
// by label. `options` = [{ value, label }].
export function finishProviderOptions(options) {
  const labelCount = new Map();
  options.forEach((o) => labelCount.set(o.label, (labelCount.get(o.label) || 0) + 1));
  return options
    .map((o) => (labelCount.get(o.label) > 1 ? { ...o, label: o.value } : o))
    .sort((a, b) => a.label.localeCompare(b.label) || a.value.localeCompare(b.value));
}

// Provider filter options from loaded rows: one { value, label } per
// provider key (value = providerKey, what the filter matches on), sorted by
// label. Two keys that would share a label keep their own keys instead.
// `doctorLabels` false (other practices): label = key, as before.
export function providerOptionsFromRows(
  rows,
  directory = EMPTY_PROVIDER_DIRECTORY,
  { doctorLabels = true } = {},
) {
  const rawByKey = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = providerKey(row, directory);
    if (!key) return;
    if (!rawByKey.has(key)) rawByKey.set(key, new Set());
    const raws = rawByKey.get(key);
    [row?.providername, row?.linked_provider].forEach((r) => {
      const t = tidy(r);
      if (t) raws.add(t);
    });
  });
  return finishProviderOptions(
    Array.from(rawByKey, ([value, raws]) => ({
      value,
      label: doctorLabels ? providerLabel(value, Array.from(raws)) : value,
    })),
  );
}

// ---------------------------------------------------------------------------
// Provider filter keyed by provider ID (owner 2026-10-08: "please make the
// filter use providerid instead of name").
//
// The same provider is stored under different name strings on different
// visits ("Reshmi Adupa, MD" through 10-16, "Reshmi Adupa" from 10-20; full
// Athena names such as "Madhu Pamganamamula, MD" come from the hub PHP
// reschedule path), so the filter's option key is the visit's EFFECTIVE
// provider id, never a name:
//   1. linked_provider that is a provider_master name (any letter case, or
//      only spaces/dots differ: "jaime", "Dr.Madhu") -> that provider's id.
//      linked_provider is a deliberate reassignment (the Select Provider
//      modal moves coverage and writes the provider_master name), so a visit
//      booked for one provider and reassigned to another counts under the
//      one it was reassigned to;
//   2. linked_provider that is a known full Athena display name (learned from
//      the loaded rows' own providername, i.e. the booked provider's Athena
//      name, when the rows carry it) -> that provider's id;
//   3. otherwise the booked providerid (every other linked_provider value in
//      9928's data is the booked provider's own Athena name);
//   4. no booked id: the linked name with credentials stripped matched to a
//      provider_master name ("Reshmi Adupa, MD" -> Reshmi Adupa);
//   5. still nothing: a name bucket ("name:<normalised name>"), so the visit
//      stays selectable instead of disappearing.
// A per-practice alias map ({ "16": "15" }: one person under two Athena
// provider ids, config/practice providerAliases) is applied to the resolved
// id, whichever step found it, before the key is formed.
// Keys: "id:<providerid>" / "name:<normalised name>". Nothing is written
// back: this only decides which filter entry a visit belongs to.
// The label is one display name per id: the provider_master name (or, for an
// id the directory does not list, the name the rows use most), with the
// existing providerLabel rules where doctorLabels is on ("Dr. Malik").

const PROVIDER_ID_PREFIX = "id:";
const PROVIDER_NAME_PREFIX = "name:";

// Only spaces, dots and letter case removed ("Dr.Madhu" = "Dr. Madhu").
export function squashProviderName(s) {
  return String(s ?? "").toLowerCase().replace(/[\s.]/g, "");
}

// A name without its title and credentials, lower case:
// "Reshmi Adupa, MD" / "Dr. Reshmi Adupa" / "RESHMI ADUPA" -> "reshmi adupa".
// A degree without a comma only counts in capitals after a mixed-case name
// (physicianSuffix), so the surname "Do" survives ("Thanh Do").
export function normProviderName(s) {
  let t = tidy(s);
  if (!t) return "";
  const degree = physicianSuffix(t);
  if (degree) t = degree[1];
  t = t.replace(/,.*$/, ""); // ", NP-C", ", APRN-CNP", ...
  t = t.replace(/\s+(?:NP-C|NP|APRN-CNP|APRN|FNP-C|FNP|PA-C|DNP)\.?$/, "");
  t = t.replace(/^dr(?:\.\s*|\s+)/i, "");
  return t.replace(/\./g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

const presentId = (v) => {
  if (v === undefined || v === null) return "";
  const s = String(v).trim();
  return s && s !== "0" ? s : "";
};
const textOf = (v) => (typeof v === "string" ? tidy(v) : "");

// Full Athena display names learned from rows that carry their booked
// provider's name (providername) next to its providerid: normalised name ->
// id, null when two ids share it. The v1 dashboard rows carry no
// providername, so there this is empty and step 3 decides.
export function learnAthenaNames(rows) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = presentId(row?.providerid);
    const norm = normProviderName(textOf(row?.providername));
    if (!id || !norm) return;
    if (!map.has(norm)) map.set(norm, id);
    else if (map.get(norm) !== id) map.set(norm, null);
  });
  return map;
}

const aliasId = (id, aliases) => {
  if (!id || !aliases) return id;
  const to = aliases[id];
  return to !== undefined && to !== null && String(to).trim() ? String(to).trim() : id;
};

// The visit's effective provider id (string), or "" when none resolves.
// `aliases`: { fromId: toId } (config/practice providerAliases).
export function providerIdOf(row, directory = EMPTY_PROVIDER_DIRECTORY, athenaNames = null, aliases = null) {
  return aliasId(resolveProviderId(row, directory, athenaNames), aliases);
}

function resolveProviderId(row, directory, athenaNames) {
  const dir = directory || EMPTY_PROVIDER_DIRECTORY;
  const linked = textOf(row?.linked_provider);
  if (linked) {
    const exact = dir.idByLowerName?.get(linked.toLowerCase());
    if (exact) return exact;
    const squashed = dir.idBySquash?.get(squashProviderName(linked));
    if (squashed) return squashed;
    const athena = athenaNames && athenaNames.get(normProviderName(linked));
    if (athena) return athena;
  }
  const booked = presentId(row?.providerid);
  if (booked) return booked;
  if (linked) {
    const byNorm = dir.idByNorm?.get(normProviderName(linked));
    if (byNorm) return byNorm;
  }
  return "";
}

// The filter key of a visit: "id:<id>", else "name:<normalised raw name>",
// else "" (a row with no provider at all, e.g. a patient-search hit with no
// visit: never an option, as before).
export function providerFilterKey(row, directory = EMPTY_PROVIDER_DIRECTORY, athenaNames = null, aliases = null) {
  const id = providerIdOf(row, directory, athenaNames, aliases);
  if (id) return `${PROVIDER_ID_PREFIX}${id}`;
  let raw = "";
  try {
    raw = rawProviderName({
      linked_provider: textOf(row?.linked_provider),
      providername: textOf(row?.providername),
      provider: textOf(row?.provider),
      provider_name: textOf(row?.provider_name),
      providerfirstname: textOf(row?.providerfirstname),
      providerlastname: textOf(row?.providerlastname),
    });
  } catch (e) {
    raw = "";
  }
  if (!raw) return "";
  return `${PROVIDER_NAME_PREFIX}${normProviderName(raw) || raw.toLowerCase()}`;
}

export const providerIdFromKey = (key) =>
  String(key ?? "").startsWith(PROVIDER_ID_PREFIX) ? String(key).slice(PROVIDER_ID_PREFIX.length) : "";

// One resolver per (directory, rows): keyOf(row) for filtering and the
// options built from the same rows, so both always agree.
//   options: [{ value: key, label, name: canonical name, names: [raw names] }]
export function providerFilterModel(
  rows,
  directory = EMPTY_PROVIDER_DIRECTORY,
  { doctorLabels = true, aliases = null } = {},
) {
  const list = Array.isArray(rows) ? rows : [];
  const dir = directory || EMPTY_PROVIDER_DIRECTORY;
  const athenaNames = learnAthenaNames(list);
  const keyOf = (row) => providerFilterKey(row, dir, athenaNames, aliases);
  const groups = new Map(); // key -> Map(raw name -> count)
  list.forEach((row) => {
    const key = keyOf(row);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, new Map());
    const names = groups.get(key);
    const add = (n) => {
      const t = textOf(n);
      if (t) names.set(t, (names.get(t) || 0) + 1);
    };
    add(row?.linked_provider);
    // The booked provider's Athena name tells that provider's credentials,
    // never those of the provider a visit was reassigned to.
    if (key === `${PROVIDER_ID_PREFIX}${aliasId(presentId(row?.providerid), aliases)}`) add(row?.providername);
  });
  const options = Array.from(groups, ([value, counts]) => {
    const names = Array.from(counts.keys()).sort();
    const id = providerIdFromKey(value);
    const mostUsed = Array.from(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    const name = (id && dir.byId.get(id)) || (mostUsed ? mostUsed[0] : "") || value;
    return {
      value,
      label: doctorLabels ? providerLabel(name, names) || name : name,
      name,
      names,
    };
  });
  return { keyOf, options: finishProviderIdOptions(options) };
}

// Two entries that would show the same label show their names instead, and
// two with the same name get their id ("Leanora (16)"), so every entry reads
// differently. Sorted by label.
export function finishProviderIdOptions(options) {
  const count = (pick) => {
    const m = new Map();
    options.forEach((o) => m.set(pick(o), (m.get(pick(o)) || 0) + 1));
    return m;
  };
  const byLabel = count((o) => o.label);
  let out = options.map((o) => (byLabel.get(o.label) > 1 ? { ...o, label: o.name } : o));
  const byName = count((o) => o.label);
  out = out.map((o) => {
    if (byName.get(o.label) <= 1) return o;
    const id = providerIdFromKey(o.value);
    return { ...o, label: `${o.label} (${id || "other"})` };
  });
  return out.sort((a, b) => a.label.localeCompare(b.label) || a.value.localeCompare(b.value));
}

// Maps a selection onto the current options: a value that is an option key
// stays; anything else - a provider NAME selected before the filter keyed by
// id, or a key whose provider left the loaded day - becomes the option that
// carries that name (label, canonical name or one of its raw names, exact
// first, then ignoring title and credentials), or is dropped. Duplicates are
// removed. Returns the same array when nothing changes.
export function migrateProviderSelection(selected, options) {
  const sel = Array.isArray(selected) ? selected : [];
  const opts = Array.isArray(options) ? options : [];
  const values = new Set(opts.map((o) => o.value));
  const namesOf = (o) => [o.label, o.name, ...(Array.isArray(o.names) ? o.names : [])].map(tidy).filter(Boolean);
  const find = (v) => {
    const lower = tidy(v).toLowerCase();
    if (!lower) return null;
    const exact = opts.find((o) => namesOf(o).some((n) => n.toLowerCase() === lower));
    if (exact) return exact.value;
    const norm = normProviderName(v);
    const loose = norm && opts.find((o) => namesOf(o).some((n) => normProviderName(n) === norm));
    return loose ? loose.value : null;
  };
  const out = [];
  sel.forEach((v) => {
    const mapped = values.has(v) ? v : String(v).startsWith(PROVIDER_ID_PREFIX) ? null : find(v);
    if (mapped && !out.includes(mapped)) out.push(mapped);
  });
  return out.length === sel.length && out.every((v, i) => v === sel[i]) ? sel : out;
}
